// Shared helpers for detecting geo-coded entities. The backend projects
// coordinates onto a `_geo_point` and also exposes the raw lat/lon under a
// handful of key spellings (camelCase, snake_case, with/without `_` prefix),
// so detection has to probe all of them.

export const LAT_KEYS = ['lat', 'latitude', 'geo_lat', '_geo_lat'] as const
export const LON_KEYS = ['lon', 'lng', 'longitude', 'geo_lon', '_geo_lon'] as const

/** Reads the first finite number found under any of `keys`, parsing strings. */
export function pickNumber(props: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = props[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return null
}

/** Extracts a valid (lat, lon) pair from entity/node properties, if present. */
export function extractGeoPoint(
  props: Record<string, unknown> | null | undefined
): { lat: number; lon: number } | null {
  if (!props) return null
  // A precomputed `_geo_point` is authoritative when present.
  const point = props._geo_point
  if (point && typeof point === 'object') {
    const lat = pickNumber(point as Record<string, unknown>, ['lat', 'latitude'])
    const lon = pickNumber(point as Record<string, unknown>, ['lon', 'lng', 'longitude'])
    if (lat != null && lon != null && isValidLatLon(lat, lon)) return { lat, lon }
  }
  const lat = pickNumber(props, LAT_KEYS)
  const lon = pickNumber(props, LON_KEYS)
  if (lat == null || lon == null || !isValidLatLon(lat, lon)) return null
  return { lat, lon }
}

/** True when the entity carries usable geo coordinates. */
export function hasGeoCoordinates(props: Record<string, unknown> | null | undefined): boolean {
  return extractGeoPoint(props) != null
}

function isValidLatLon(lat: number, lon: number): boolean {
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
}
